import { useEffect, useRef, useState } from 'react';
import { Bars2Icon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import { Odontogram } from '@odo/shared-ui/clinical/elastics/Odontogram';
import { MAX_ELASTIC_ALIGNER } from '@odo/shared-ui/clinical/elastics/model';
import type { ElasticPlacement, ElasticScheme } from '@odo/shared-ui/clinical/elastics/model';
import {
    alignerRangeErrorMessage,
    createElasticScheme,
    elasticSchemeName,
    elasticSchemeRangeLabel,
    isAlignerNumberValid
} from '@odo/shared-ui/clinical/elastics/persistence';

interface ElasticSchemesEditorProps {
    schemes: ElasticScheme[];
    onChange: (next: ElasticScheme[]) => void;
}

/** Whether a typed draft is a real aligner number (a finite value of at least 1). */
function isValidAlignerDraft(raw: string): boolean {
    if (raw.trim() === '') return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1;
}

/** `0` marks a bound the doctor has cleared and not yet retyped - see
 *  `isAlignerRangeValid` in `persistence.ts` for why 0 is a safe sentinel. */
const alignerDraftFromValue = (value: number): string => (value === 0 ? '' : String(value));

// Aligner bound input with its own draft text, so a cleared field stays empty (sentinel 0)
// instead of snapping back; the missing range is enforced by blocking submit.
function AlignerNumberInput({
    label,
    value,
    htmlMin,
    invalid,
    onCommit
}: {
    label: string;
    value: number;
    htmlMin: number;
    invalid?: boolean;
    onCommit: (value: number) => void;
}) {
    const [draft, setDraft] = useState(alignerDraftFromValue(value));
    // The value this instance last committed: a matching prop change keeps the typed text
    // (e.g. "05"), any other change (switching schemes) resyncs the draft.
    const lastCommitted = useRef(value);

    useEffect(() => {
        if (value !== lastCommitted.current) setDraft(alignerDraftFromValue(value));
        lastCommitted.current = value;
    }, [value]);

    const commit = (next: number) => {
        lastCommitted.current = next;
        onCommit(next);
    };

    return (
        <label className="flex flex-col text-[11px] font-medium text-gray-500">
            {label}
            <input
                type="number"
                min={htmlMin}
                max={MAX_ELASTIC_ALIGNER}
                value={draft}
                onChange={(e) => {
                    const raw = e.target.value;
                    setDraft(raw);
                    if (raw.trim() === '') commit(0);
                    else if (isValidAlignerDraft(raw)) commit(Number(raw));
                }}
                className={`${baseFieldClasses} mt-1 h-8 w-20 py-0 text-sm ${
                    invalid ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
            />
        </label>
    );
}

// Tabbed editor for elastics schemes, each pinned to an aligner span with its own
// odontogram placements.
export function ElasticSchemesEditor({ schemes, onChange }: ElasticSchemesEditorProps) {
    const [activeId, setActiveId] = useState<string | null>(null);
    // Tab drag-reorder feedback: the dragged scheme id and the hovered drop target.
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);
    const activeScheme = schemes.find((s) => s.id === activeId) ?? schemes[0];
    const rangeError = activeScheme
        ? alignerRangeErrorMessage(activeScheme.from, activeScheme.to)
        : undefined;
    const updateScheme = (id: string, patch: Partial<ElasticScheme>) => {
        onChange(schemes.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    };

    const updateElastics = (id: string, elastics: ElasticPlacement[]) => {
        updateScheme(id, { elastics });
    };

    // Keeps an auto-generated range name in sync with the range until the doctor renames it.
    const changeRange = (scheme: ElasticScheme, patch: { from?: number; to?: number }) => {
        const from = patch.from ?? scheme.from;
        const to = patch.to ?? scheme.to;
        const isAutoNamed =
            scheme.name.trim() === '' || scheme.name.trim() === elasticSchemeRangeLabel(scheme);
        const name = isAutoNamed ? elasticSchemeRangeLabel({ ...scheme, from, to }) : scheme.name;
        updateScheme(scheme.id, { from, to, name });
    };

    // Reordering moves content between slots but pins each slot's aligner range to its
    // position, re-deriving auto names like `changeRange` does.
    const reassignRangesToSlots = (reordered: ElasticScheme[]): ElasticScheme[] =>
        reordered.map((scheme, i) => {
            const { from, to } = schemes[i];
            const isAutoNamed =
                scheme.name.trim() === '' || scheme.name.trim() === elasticSchemeRangeLabel(scheme);
            const name = isAutoNamed
                ? elasticSchemeRangeLabel({ ...scheme, from, to })
                : scheme.name;
            return { ...scheme, from, to, name };
        });

    const removeScheme = (id: string) => {
        const next = schemes.filter((s) => s.id !== id);
        onChange(next);
        if (activeId === id) setActiveId(next[0]?.id ?? null);
    };

    const addScheme = () => {
        const scheme = createElasticScheme(schemes[schemes.length - 1]);
        onChange([...schemes, scheme]);
        setActiveId(scheme.id);
    };

    // Dropping swaps exactly two positions; a splice reinsertion would shift every tab between.
    const reorderSchemes = (draggedSchemeId: string, targetSchemeId: string) => {
        if (draggedSchemeId === targetSchemeId) return;
        const fromIndex = schemes.findIndex((s) => s.id === draggedSchemeId);
        const toIndex = schemes.findIndex((s) => s.id === targetSchemeId);
        if (fromIndex === -1 || toIndex === -1) return;
        const next = [...schemes];
        [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
        onChange(reassignRangesToSlots(next));
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Scheme tabs */}
            <div
                className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2"
                role="tablist"
            >
                {schemes.map((scheme) => {
                    const isActive = scheme.id === activeScheme?.id;
                    const isDragged = scheme.id === draggedId;
                    const isDropTarget = scheme.id === dropTargetId && scheme.id !== draggedId;
                    return (
                        <button
                            key={scheme.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveId(scheme.id)}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', scheme.id);
                                e.dataTransfer.effectAllowed = 'move';
                                setDraggedId(scheme.id);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                if (draggedId && draggedId !== scheme.id)
                                    setDropTargetId(scheme.id);
                            }}
                            onDragLeave={() => {
                                setDropTargetId((current) =>
                                    current === scheme.id ? null : current
                                );
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (draggedId) reorderSchemes(draggedId, scheme.id);
                                setDraggedId(null);
                                setDropTargetId(null);
                            }}
                            onDragEnd={() => {
                                setDraggedId(null);
                                setDropTargetId(null);
                            }}
                            className={`relative flex cursor-grab items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors active:cursor-grabbing ${
                                isActive
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } ${isDragged ? 'opacity-40' : ''} ${
                                isDropTarget ? 'ring-2 ring-gray-400 ring-offset-1' : ''
                            }`}
                        >
                            <Bars2Icon className="h-3 w-3 shrink-0 text-gray-400" />
                            <span>{elasticSchemeName(scheme)}</span>
                            {elasticSchemeName(scheme) !== elasticSchemeRangeLabel(scheme) && (
                                <span className={isActive ? 'text-gray-300' : 'text-gray-400'}>
                                    {elasticSchemeRangeLabel(scheme)}
                                </span>
                            )}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={addScheme}
                    className="flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50"
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add schema
                </button>
            </div>

            {/* Active scheme */}
            {activeScheme && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <label className="flex flex-col text-[11px] font-medium text-gray-500">
                            Schema name
                            <input
                                type="text"
                                value={activeScheme.name}
                                onChange={(e) =>
                                    updateScheme(activeScheme.id, { name: e.target.value })
                                }
                                placeholder={elasticSchemeRangeLabel(activeScheme)}
                                maxLength={200}
                                className={`${baseFieldClasses} mt-1 h-8 w-64 py-0 text-sm`}
                            />
                        </label>
                        <div className="flex flex-wrap items-end gap-3">
                            <AlignerNumberInput
                                label="From aligner"
                                value={activeScheme.from}
                                htmlMin={1}
                                invalid={!isAlignerNumberValid(activeScheme.from)}
                                onCommit={(from) => changeRange(activeScheme, { from })}
                            />
                            <AlignerNumberInput
                                label="To aligner"
                                value={activeScheme.to}
                                htmlMin={activeScheme.from}
                                invalid={
                                    !isAlignerNumberValid(activeScheme.to) ||
                                    (activeScheme.from >= 1 && activeScheme.to < activeScheme.from)
                                }
                                onCommit={(to) => changeRange(activeScheme, { to })}
                            />
                            <button
                                type="button"
                                onClick={() => removeScheme(activeScheme.id)}
                                className="mb-0.5 flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2 text-xs font-medium text-gray-500 transition-colors hover:border-red-300 hover:text-red-600"
                            >
                                <TrashIcon className="h-3.5 w-3.5" />
                                Remove schema
                            </button>
                        </div>
                    </div>

                    {rangeError && (
                        <p className="text-[11px] font-medium text-red-600">{rangeError}</p>
                    )}

                    <Odontogram
                        placements={activeScheme.elastics}
                        onChange={(next) => updateElastics(activeScheme.id, next)}
                    />
                </div>
            )}
        </div>
    );
}
